import { useRef } from "react";
import type {
  ArtworkLayer,
  DesignSide,
  Fill,
  LayerType,
  TextStyle,
  WearableConfig,
} from "../types";
import {
  getShirtAxes,
  shirtToTexture,
  textureToShirt,
} from "../utils/shirtAxes";
import { measureText } from "../editor/textureComposer";
import { FillEditor } from "./FillEditor";

/**
 * A curated set of fonts that are either system-installed on most
 * OSes or fall back sensibly. Picked to cover the common "logo /
 * streetwear / decorative" bases for a wearable design tool.
 */
/**
 * Accept either a modern `Fill` value or a legacy string color and
 * return a normalized `Fill`. Keeps the control robust if someone
 * passes a plain color string during migration.
 */
function toFill(c: Fill | string | undefined): Fill {
  if (!c) return { kind: "solid", color: "#ffffff" };
  if (typeof c === "string") return { kind: "solid", color: c };
  return c;
}

const FONT_FAMILIES: { id: string; label: string; css: string }[] = [
  { id: "impact", label: "Impact", css: "Impact, 'Arial Black', sans-serif" },
  { id: "arial", label: "Arial", css: "Arial, Helvetica, sans-serif" },
  {
    id: "helvetica",
    label: "Helvetica",
    css: "Helvetica, Arial, sans-serif",
  },
  { id: "verdana", label: "Verdana", css: "Verdana, Geneva, sans-serif" },
  {
    id: "trebuchet",
    label: "Trebuchet MS",
    css: "'Trebuchet MS', Verdana, sans-serif",
  },
  { id: "georgia", label: "Georgia", css: "Georgia, 'Times New Roman', serif" },
  {
    id: "times",
    label: "Times New Roman",
    css: "'Times New Roman', Times, serif",
  },
  {
    id: "courier",
    label: "Courier",
    css: "'Courier New', Courier, monospace",
  },
  { id: "comic", label: "Comic Sans", css: "'Comic Sans MS', cursive" },
  { id: "brush", label: "Brush Script", css: "'Brush Script MT', cursive" },
  {
    id: "monospace",
    label: "Monospace",
    css: "ui-monospace, Menlo, Consolas, monospace",
  },
];

interface Props {
  layer: ArtworkLayer;
  config: WearableConfig;
  onChange: (patch: Partial<ArtworkLayer>) => void;
  onRemove: () => void;
  /** Duplicate this layer onto the opposite side of the shirt. */
  onDuplicate: () => void;
}

export function LayerControls({
  layer,
  config,
  onChange,
  onRemove,
  onDuplicate,
}: Props) {
  // Sliders operate in shirt space so "X" moves left/right on the 3D
  // shirt (not along the texture-pixel axis) and "Y" moves up/down.
  //
  // Range = ±textureSize on each axis. A 1024×1024 texture means the
  // user can reach any pixel no matter where the box centre sits or
  // how the shirt basis is rotated in UV space. Placement is always
  // freeform — the box only defines the spawn centre (slider 0,0) and
  // the shirt-axis basis; it no longer clips the drawing.
  const box = layer.side === "front" ? config.frontBox : config.backBox;
  const axes = getShirtAxes(box);
  const range = config.textureSize;
  const sMin = -range;
  const sMax = range;
  const tMin = -range;
  const tMax = range;

  const shirt = textureToShirt(layer.x, layer.y, axes);
  const setShirt = (sx: number, sy: number) => {
    const { x, y } = shirtToTexture(sx, sy, axes);
    onChange({ x: Math.round(x), y: Math.round(y) });
  };

  const isText = layer.kind === "text" && layer.text !== undefined;

  // Patch a text style and keep the layer's natural bbox in sync so
  // the selection outline + hit tests track the rasterised glyphs.
  const setTextStyle = (patch: Partial<TextStyle>) => {
    if (!isText) return;
    const next: TextStyle = { ...(layer.text as TextStyle), ...patch };
    const { width, height } = measureText(next);
    onChange({
      text: next,
      naturalWidth: width,
      naturalHeight: height,
      // Keep the layer's list name roughly in sync with the string.
      // Collapse newlines so the layer list stays single-line.
      name:
        next.text.replace(/\s+/g, " ").trim().slice(0, 48) || "Text",
    });
  };

  return (
    <div className="controls">
      <div className="row">
        <label className="label">Name</label>
        <input
          className="input"
          value={layer.name}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </div>

      <div className="row">
        <label className="label">Side</label>
        <div className="seg">
          {(["front", "back"] as DesignSide[]).map((s) => (
            <button
              key={s}
              type="button"
              className={`seg-btn ${layer.side === s ? "active" : ""}`}
              onClick={() => {
                // Re-center on the new box so the layer actually appears
                // on that side instead of sitting in the old coords.
                const nextBox = s === "front" ? config.frontBox : config.backBox;
                onChange({
                  side: s,
                  x: Math.round(nextBox.x + nextBox.width / 2),
                  y: Math.round(nextBox.y + nextBox.height / 2),
                });
              }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {isText && layer.text && (
        <>
          <div className="row">
            <label className="label">Text</label>
            <textarea
              className="input"
              style={{
                gridColumn: "2 / span 2",
                resize: "vertical",
                minHeight: 60,
                fontFamily: "inherit",
                lineHeight: 1.3,
              }}
              rows={3}
              placeholder="Press Enter for a new line"
              value={layer.text.text}
              onChange={(e) => setTextStyle({ text: e.target.value })}
            />
          </div>

          <div className="row">
            <label className="label">Font</label>
            <select
              className="input"
              style={{ gridColumn: "2 / span 2" }}
              value={
                FONT_FAMILIES.find((f) => f.css === layer.text!.fontFamily)
                  ?.id ?? "custom"
              }
              onChange={(e) => {
                const fam = FONT_FAMILIES.find((f) => f.id === e.target.value);
                if (fam) setTextStyle({ fontFamily: fam.css });
              }}
            >
              {FONT_FAMILIES.map((f) => (
                <option
                  key={f.id}
                  value={f.id}
                  style={{ fontFamily: f.css }}
                >
                  {f.label}
                </option>
              ))}
              {!FONT_FAMILIES.find((f) => f.css === layer.text!.fontFamily) && (
                <option value="custom">Custom</option>
              )}
            </select>
          </div>

          <div className="row">
            <label className="label">Style</label>
            <div className="seg" style={{ gridColumn: "2 / span 2" }}>
              <button
                type="button"
                className={`seg-btn ${layer.text.bold ? "active" : ""}`}
                style={{ fontWeight: 700 }}
                onClick={() => setTextStyle({ bold: !layer.text!.bold })}
              >
                Bold
              </button>
              <button
                type="button"
                className={`seg-btn ${layer.text.italic ? "active" : ""}`}
                style={{ fontStyle: "italic" }}
                onClick={() => setTextStyle({ italic: !layer.text!.italic })}
              >
                Italic
              </button>
            </div>
          </div>

          <NumberRow
            label="Font Size"
            value={layer.text.fontSize}
            min={8}
            max={512}
            step={1}
            onChange={(v) => setTextStyle({ fontSize: Math.max(1, v) })}
          />

          <NumberRow
            label="Tracking"
            value={layer.text.letterSpacing}
            min={-40}
            max={120}
            step={1}
            fineStep={0.5}
            onChange={(v) => setTextStyle({ letterSpacing: v })}
          />

          <div className="row">
            <label className="label">Tilt Mode</label>
            <div className="seg" style={{ gridColumn: "2 / span 2" }}>
              {(
                [
                  { id: "uniform", label: "Same" },
                  { id: "alternate", label: "Alternate" },
                ] as { id: "uniform" | "alternate"; label: string }[]
              ).map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={`seg-btn ${
                    (layer.text!.letterTiltMode ?? "uniform") === m.id
                      ? "active"
                      : ""
                  }`}
                  onClick={() => setTextStyle({ letterTiltMode: m.id })}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <NumberRow
            label="Letter Tilt"
            value={layer.text.letterTilt}
            min={-45}
            max={45}
            step={1}
            fineStep={0.5}
            onChange={(v) => setTextStyle({ letterTilt: v })}
          />

          <NumberRow
            label="Line Height"
            value={layer.text.lineHeight}
            min={0.5}
            max={3}
            step={0.05}
            fineStep={0.01}
            onChange={(v) => setTextStyle({ lineHeight: v })}
          />

          <div className="row" style={{ alignItems: "flex-start" }}>
            <label className="label" style={{ paddingTop: 4 }}>
              Color
            </label>
            <div style={{ gridColumn: "2 / span 2" }}>
              <FillEditor
                value={toFill(layer.text.color)}
                onChange={(f: Fill) => setTextStyle({ color: f })}
              />
            </div>
          </div>
        </>
      )}

      <NumberRow
        label="Y"
        value={Math.round(shirt.sy)}
        min={tMin}
        max={tMax}
        step={1}
        onChange={(v) => setShirt(shirt.sx, v)}
      />
      <NumberRow
        label="X"
        value={Math.round(shirt.sx)}
        min={sMin}
        max={sMax}
        step={1}
        onChange={(v) => setShirt(v, shirt.sy)}
      />
      <NumberRow
        label="Scale"
        value={layer.scale}
        min={-2}
        max={2}
        step={0.05}
        fineStep={0.005}
        onChange={(v) => onChange({ scale: v })}
      />
      <NumberRow
        label="Rotation"
        value={layer.rotation}
        min={-180}
        max={180}
        step={1}
        onChange={(v) => onChange({ rotation: v })}
      />
      <NumberRow
        label="Opacity"
        value={layer.opacity}
        min={0}
        max={1}
        step={0.01}
        onChange={(v) => onChange({ opacity: v })}
      />

      <div className="row">
        <label className="label">Type</label>
        <div className="seg" style={{ gridColumn: "2 / span 2" }}>
          {(
            [
              { id: "mix", label: "Mix" },
              { id: "multiply", label: "Multiply" },
              { id: "emission", label: "Emission" },
            ] as { id: LayerType; label: string }[]
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              className={`seg-btn ${layer.type === t.id ? "active" : ""}`}
              onClick={() => onChange({ type: t.id })}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {layer.type === "emission" && (
        <NumberRow
          label="Emission"
          value={layer.emissiveIntensity}
          min={0}
          max={2}
          step={0.05}
          fineStep={0.005}
          onChange={(v) => onChange({ emissiveIntensity: v })}
        />
      )}

      <div className="row">
        <label className="label">Visible</label>
        <input
          type="checkbox"
          checked={layer.visible}
          onChange={(e) => onChange({ visible: e.target.checked })}
        />
      </div>

      <div className="row">
        <button
          type="button"
          className="btn full"
          onClick={onDuplicate}
          title={`Create a copy of this layer on the ${
            layer.side === "front" ? "back" : "front"
          } side`}
        >
          Duplicate to {layer.side === "front" ? "Back" : "Front"}
        </button>
      </div>

      <div className="row">
        <button type="button" className="btn danger full" onClick={onRemove}>
          Delete Layer
        </button>
      </div>

      <div className="meta">
        Natural size: {layer.naturalWidth}×{layer.naturalHeight}px
      </div>
    </div>
  );
}

interface NumberRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  /**
   * Step size used when the user holds Shift while pressing arrow keys
   * on the slider. Defaults to `step / 10` so Shift always moves slower
   * than a normal arrow-key press.
   */
  fineStep?: number;
  onChange: (v: number) => void;
}

function NumberRow({
  label,
  value,
  min,
  max,
  step,
  fineStep,
  onChange,
}: NumberRowProps) {
  const slow = fineStep ?? step / 10;
  const slowFactor = step > 0 ? slow / step : 1;

  // Holding Shift while pressing arrow keys nudges by `slow` instead of
  // the browser default — which on `<input type="number">` is 10×step
  // (fast) and on `<input type="range">` is just step. We override BOTH
  // inputs so Shift consistently means "fine-tune / move slower".
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!e.shiftKey) return;
    let delta = 0;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") delta = slow;
    else if (e.key === "ArrowLeft" || e.key === "ArrowDown") delta = -slow;
    else return;
    e.preventDefault();
    const next = Math.max(min, Math.min(max, value + delta));
    onChange(Number(next.toFixed(6)));
  };

  // Shift + mouse-drag on a range slider: native `<input type="range">`
  // doesn't respect the Shift modifier while dragging — it just moves at
  // the full `step` per pixel. We hijack the drag when Shift is held at
  // pointer-down and compute the value ourselves from the pointer's X
  // position, scaled down by `slow/step` so the drag feels slower/finer.
  const dragRef = useRef<{
    pointerId: number;
    startClientX: number;
    startValue: number;
  } | null>(null);

  const onRangePointerDown = (e: React.PointerEvent<HTMLInputElement>) => {
    if (!e.shiftKey || slowFactor >= 1) return;
    e.preventDefault();
    const el = e.currentTarget;
    dragRef.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startValue: value,
    };
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    el.focus();
  };

  const onRangePointerMove = (e: React.PointerEvent<HTMLInputElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const width = Math.max(1, rect.width);
    const dx = e.clientX - drag.startClientX;
    // Map pixel delta -> value delta at native speed, then scale down.
    const deltaValue = (dx / width) * (max - min) * slowFactor;
    const next = Math.max(min, Math.min(max, drag.startValue + deltaValue));
    onChange(Number(next.toFixed(6)));
  };

  const onRangePointerUp = (e: React.PointerEvent<HTMLInputElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="row">
      <label className="label">{label}</label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        onKeyDown={onKey}
        onPointerDown={onRangePointerDown}
        onPointerMove={onRangePointerMove}
        onPointerUp={onRangePointerUp}
        onPointerCancel={onRangePointerUp}
        className="range"
        title="Hold Shift (and drag, or use arrow keys) for fine adjustment"
      />
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => {
          const n = parseFloat(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
        onKeyDown={onKey}
        className="num"
        title="Hold Shift + arrow keys for fine adjustment"
      />
    </div>
  );
}
